import { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2, Building2, MapPin, Mail, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

// V2 True Glass modal for the Enterprise "Request Operational Audit"
// CTA. Backdrop blur pinned to 16px per the mobile performance brief.
// Zero em-dashes anywhere in the copy or code, per the style guide.
export default function EnterpriseAuditModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", num_locations: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", company: "", email: "", num_locations: "" });
      setSent(false);
      // Lock body scroll while modal is open so mobile does not double scroll.
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = form.name.trim() && form.company.trim() && form.email.trim() && Number(form.num_locations) >= 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/leads/enterprise", {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim().toLowerCase(),
        num_locations: Math.max(1, Number(form.num_locations) || 1),
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit request. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
      data-testid="enterprise-modal"
    >
      {/* Dim scrim: solid overlay, no heavy backdrop-filter here so scrolling stays crisp on mobile.
          Clicking the scrim closes the modal. */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(6,4,3,0.72)" }}
        onClick={onClose}
        data-testid="enterprise-modal-scrim"
      />

      {/* Glass card: 16px backdrop-blur only, gold hairline, subtle inner highlight */}
      <div
        className="relative w-full max-w-lg rounded-[24px] p-7 md:p-9"
        style={{
          background: "linear-gradient(180deg, rgba(24,17,10,0.72), rgba(14,10,7,0.82))",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(232,207,154,0.28)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(232,207,154,0.06)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white/90 hover:bg-white/10 transition-colors"
          aria-label="Close"
          data-testid="enterprise-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        {sent ? (
          <div className="text-center py-4" data-testid="enterprise-modal-sent">
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ background: "rgba(120,200,140,0.14)", border: "1px solid rgba(120,200,140,0.4)" }}
            >
              <CheckCircle2 className="w-6 h-6" style={{ color: "#8dd3a4" }} />
            </div>
            <h3 className="font-display text-2xl mb-2">Request received</h3>
            <p className="text-sm text-white/70 leading-relaxed">
              Your operational audit brief has landed. A CulinaireSpecialist strategist will reach out within one business day to schedule the discovery call.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 px-5 py-2.5 rounded-full text-sm font-medium transition-all"
              style={{
                background: "linear-gradient(180deg, rgba(232,207,154,0.22), rgba(200,169,106,0.08))",
                border: "1px solid rgba(232,207,154,0.5)",
                color: "#f5efe0",
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="text-[0.55rem] uppercase tracking-[0.28em] mb-2" style={{ color: "rgba(232,207,154,0.85)" }}>
              Enterprise : Operational Audit
            </div>
            <h3 className="font-display text-3xl leading-tight mb-2">Request your audit</h3>
            <p className="text-sm text-white/60 mb-6 leading-relaxed">
              We map your current cost, labor and inventory workflow across every location, then walk you through the exact recovery model your operations team will run.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="enterprise-modal-form">
              <Field
                icon={User}
                label="Your name"
                value={form.name}
                onChange={(v) => set("name", v)}
                placeholder="Alex Morgan"
                testId="enterprise-name"
                autoFocus
              />
              <Field
                icon={Building2}
                label="Company"
                value={form.company}
                onChange={(v) => set("company", v)}
                placeholder="The Waldorf Group"
                testId="enterprise-company"
              />
              <Field
                icon={MapPin}
                label="Number of locations"
                value={form.num_locations}
                onChange={(v) => set("num_locations", v.replace(/[^0-9]/g, ""))}
                placeholder="12"
                inputMode="numeric"
                testId="enterprise-locations"
              />
              <Field
                icon={Mail}
                label="Work email"
                value={form.email}
                onChange={(v) => set("email", v)}
                placeholder="alex@waldorfgroup.com"
                type="email"
                testId="enterprise-email"
              />

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full mt-2 px-6 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: "linear-gradient(180deg, rgba(232,207,154,0.38), rgba(200,169,106,0.14))",
                  border: "1px solid rgba(232,207,154,0.55)",
                  color: "#f5efe0",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), 0 10px 30px -12px rgba(200,169,106,0.35)",
                }}
                data-testid="enterprise-submit"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting
                  </span>
                ) : (
                  "Request Operational Audit"
                )}
              </button>

              <p className="text-[10px] text-white/40 text-center leading-relaxed">
                Your details are used only to schedule the audit. No third-party marketing lists, no automated outreach.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, type = "text", inputMode, testId, autoFocus }) {
  return (
    <label className="block">
      <div className="text-[0.55rem] uppercase tracking-[0.28em] text-white/50 mb-1.5 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <input
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none transition-colors"
        style={{
          background: "rgba(0,0,0,0.28)",
          border: "1px solid rgba(232,207,154,0.18)",
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(232,207,154,0.55)"; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(232,207,154,0.18)"; }}
      />
    </label>
  );
}
